import { ArrowRightIcon, BellIcon } from 'lucide-preact';
import { FunctionComponent, JSX } from 'preact';

import route from '@/lib/route';
import { BellNotification } from '@/queries/notifications/types';
import { trackMP } from '@/shared/mp';

/**
 * The label on the card's call to action.
 *
 * "My Account" rather than "About Me", matching the header menu's wording for the same
 * destination — even though the link lands on the About Me tab specifically, which is where the
 * reward is claimed.
 */
const ACTION_LABEL = 'My Account';

interface PinnedNotificationProps {
  notification: BellNotification;
  onClick: (id: string) => void;
}

/**
 * A pinned notification, drawn above everything else in the bell.
 *
 * Deliberately not the `Notification` row: this has no timestamp, no title/body split and no unread
 * dot, so the row component cannot produce it. It is the announcement card's anatomy — a coloured
 * wrapper around an inner panel, a filled circle with a white glyph, and an underlined call to
 * action — in beige, and it is copied rather than shared because `AnnouncementItem` takes an
 * announcement and writes to the announcement viewed mutation.
 */
const PinnedNotification: FunctionComponent<PinnedNotificationProps> = ({ notification, onClick }) => {
  const onClickHandler: JSX.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    onClick(notification._id);
    trackMP('bell-notif-click', {
      notificationId: notification._id,
      title: notification.title,
      description: notification.body,
      viewed: notification.isViewed,
      type: notification.type,
      actionURL: notification.actionURL,
      // The one property that separates this event from the same event fired by the ordinary row,
      // which is otherwise identical — so the two can be told apart in the funnel.
      isPinned: true,
    });

    route(notification.actionURL, false, true);
  };

  return (
    <a
      href={notification.actionURL}
      className="preflight preflight-a block rounded-lg bg-primary-green p-0.5"
      onClick={onClickHandler}
    >
      <div className="preflight flex rounded-md bg-beige-light px-2 py-3">
        <div className="preflight mr-2">
          <div className="preflight flex size-10 items-center justify-center rounded-full bg-primary-green">
            <span className="sr-only">{notification.type}</span>
            <BellIcon className="size-5 text-white" />
          </div>
        </div>

        <div className="grow">
          <h4 className="preflight preflight-h4 text-pretty text-sm font-semibold">{notification.title}</h4>
          <p className="preflight preflight-p mt-1 flex items-center text-xs">
            <span className="font-semibold text-stone-900 underline">{ACTION_LABEL}</span>
            <ArrowRightIcon className="ml-2 inline-block size-4 text-stone-900" />
          </p>
        </div>
      </div>
    </a>
  );
};

export default PinnedNotification;
